/*
 * Deskon - the "Add to cart" bar at the foot of a product card adds its variant to the cart.
 *
 * snippets/quick-add.liquid renders ONE control, and the same one for every available product,
 * whether it is sold in a single variant or in twenty:
 *
 *     <button type="button" class="dk-card-add" data-dk-card-add
 *             data-variant-id="..." data-product-url="...">Add to cart</button>
 *
 * and this file is the whole of its behaviour. The id it carries is always
 * product.selected_or_first_available_variant.id - the variant the card is displaying, the same
 * one the card's own link points at - so the button adds what the visitor is looking at.
 *
 * A PRODUCT SOLD IN SEVERAL VARIANTS THEREFORE ADDS THAT FIRST COMBINATION, not one the customer
 * chose: the card offers no way to choose. That is a known limitation rather than an oversight,
 * and the answer to it, if it ever matters, is Shopify's own quick-add dialog - not this file.
 *
 * THE CART PATH IS COPIED FROM assets/wishlist.js, whose own header records that it is a deliberate
 * copy of assets/product-form.js's submit path. The same three things are reproduced here, because
 * they are what the rest of the theme reads:
 *
 *   1 THE sections FIELD. Every <cart-items-component> in the page publishes a data-section-id;
 *     those ids are collected into the request and the server returns them rendered in
 *     response.sections. Without the field the cart drawer and the header badge have nothing to
 *     redraw themselves from.
 *   2 THE HEADERS. Accept: text/html together with X-Requested-With: XMLHttpRequest - the pair
 *     fetchConfig('javascript') builds and product-form.js then overrides. The response is JSON
 *     either way, which is why the handler below parses it as JSON.
 *   3 THE EVENT. An Event named "cart:update" constructed with { bubbles: true } - composed is left
 *     at its default of false, exactly as in wishlist.js - carrying a detail of
 *     { resource, sourceId, data } and dispatched on document.documentElement so it reaches the
 *     listeners bound to document. That dispatch is what opens the cart drawer, so this button
 *     opens the same drawer the wishlist's own add button opens. The event is built by hand rather
 *     than imported from @theme/events so this file does not depend on the import map.
 *
 * THE ROUTE IS Shopify.routes.root + 'cart/add.js', reached the same way wishlist.js reaches it:
 * the Theme global that product-form.js closes over is not exposed on window.
 */
(function () {
  const BUTTON_SELECTOR = '[data-dk-card-add]';
  const SOURCE_ID = 'deskon-card-add';

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Sends the visitor to the product's own page, or does nothing when the attribute is absent.
     Every branch that cannot finish the add ends here, so the click always has an outcome. */
  function goToProduct(button) {
    const url = button.getAttribute('data-product-url');

    if (url) window.location.href = url;
  }

  /* The section ids that go up with the request, collected exactly as wishlist.js collects them:
     every <cart-items-component> in the document, its data-section-id, empties dropped. */
  function sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  /* The same event wishlist.js dispatches, on the same element, with the same detail shape. Only
     the two source strings differ, so a listener can tell the two buttons apart. */
  function announce(response) {
    const event = new Event('cart:update', { bubbles: true });

    event.detail = {
      resource: {},
      sourceId: SOURCE_ID,
      data: {
        source: SOURCE_ID,
        itemCount: 1,
        productId: response && response.product_id ? String(response.product_id) : '',
        sections: response && response.sections,
      },
    };

    document.documentElement.dispatchEvent(event);
  }

  /* Resolves with the parsed cart/add.js response, and rejects on a network failure or on a
     response the server marked as an error. */
  function addToCart(variantId) {
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');

    const ids = sectionIds();
    if (ids.length) body.append('sections', ids.join(','));

    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: body,
    }).then((response) => {
      /* cart/add.js answers a variant it will not add - sold out, or gone since the card was
         rendered - with a 4xx whose JSON body carries a status field. wishlist.js reads that
         status out of the parsed body; this reads the same refusal off the Response one step
         earlier and throws, so that the caller's single catch covers a refusal and a dropped
         connection alike. */
      if (!response.ok) throw new Error('cart/add.js refused variant ' + variantId);

      return response.json();
    });
  }

  /*
   * ONE DELEGATED LISTENER ON document, NOT ONE PER BUTTON. The collection grid is replaced
   * wholesale when the visitor paginates or changes a filter - paginated-list.js morphs a freshly
   * rendered section in - and adding to the cart morphs the header for the same reason
   * assets/wishlist.js describes at length. A listener bound to a button at load time would stay
   * bound to a node that is no longer in the page, and the replacement button would do nothing.
   * document is never replaced, so one listener here covers every card, before and after any
   * re-render.
   */
  document.addEventListener('click', function (event) {
    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;

    /* Cancels the click's default action before anything else runs. Everything below is
       asynchronous, so a default action left in place would be taken while the request was still
       in flight. */
    event.preventDefault();

    /* THE DOUBLE-CLICK GUARD IS THE aria-busy ATTRIBUTE, and it is not only a flag read here:
       snippets/quick-add.liquid's own stylesheet carries
       `product-card .dk-card-add[aria-busy='true'] { opacity: 0.6; pointer-events: none; }`,
       so setting it dims the button and stops it receiving pointer events at the same time. This
       check is what covers the clicks that arrive by keyboard, which pointer-events does not. */
    if (button.hasAttribute('aria-busy')) return;

    const variantId = Number(button.getAttribute('data-variant-id'));

    /* A missing attribute reads back as null and a non-numeric one as NaN; both land here, as does
       a zero. Rather than dropping the click on the floor, the visitor is sent to the product page,
       where the variant can be chosen and added properly - a button that does nothing at all is
       indistinguishable from a broken card. */
    if (!Number.isFinite(variantId) || variantId <= 0) {
      goToProduct(button);
      return;
    }

    button.setAttribute('aria-busy', 'true');

    addToCart(variantId)
      .then(announce)
      .catch(function () {
        /* A refused add and a failed request both end on the product page. The card has no room to
           explain that the last one just sold, and the product page states the stock position and
           offers the other variants; leaving the customer on a silently unchanged card does not. */
        goToProduct(button);
      })
      .finally(function () {
        /* Runs whichever way the promise settled. On the failing branch the navigation above is
           already under way and this simply undoes the dimming for however long the old page is
           still on screen. */
        button.removeAttribute('aria-busy');
      });
  });
})();
